// Internal-link target helpers for the tenant runtime.
//
// A link target is "/slug" (page top), "/slug#sectionId" (section on a page),
// or "#sectionId" (section on the current page). This mirrors main-frontend's
// parseLinkTarget (src/components/WebsiteBuilder/settings/hooks/useSectionAnchors.ts)
// — the two repos build separate bundles, so keep these in lockstep by hand.

export type LinkTarget = { slug?: string; hash?: string };

export function parseLinkTarget(url: string | undefined | null): LinkTarget {
  if (!url) return {};
  const i = url.indexOf("#");
  if (i === -1) return { slug: url };
  const slug = url.slice(0, i);
  const hash = url.slice(i + 1);
  const result: LinkTarget = {};
  if (slug) result.slug = slug;
  if (hash) result.hash = hash;
  return result;
}

// Section id to scroll to once the next navigation's page has rendered. Set by
// navigateOrScroll for cross-page jumps, consumed by the hash-on-load effect in
// RuntimePage.
let pendingScroll: string | null = null;

/**
 * L2: PEEK, do not consume.
 *
 * The scroll effect in App runs on the pathname change while the OLD page is
 * still rendered — `loading` has not flipped yet. A take-once read handed the
 * id to THAT run, which could not find the element (wrong page), started
 * polling, and was then cancelled by the next render's cleanup. By the time
 * the target page mounted the id was gone and `location.hash` is empty for a
 * "/slug#section" nav link, so the guest landed at the top of the page.
 *
 * Peeking lets every re-run see the same id until one of them actually
 * scrolls; {@link clearPendingScroll} is what ends it.
 */
export function peekPendingScroll(): string | null {
  return pendingScroll;
}

/** L2: clear the pending id — on a successful scroll, or on giving up, so it
 *  can never leak into a later navigation. */
export function clearPendingScroll(): void {
  pendingScroll = null;
}

function scrollToId(id: string): void {
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Resolve an internal link click.
 *  - same-page section (no slug, or the target element is already mounted) →
 *    scroll in place
 *  - otherwise → navigate to the page; if the href carries a section hash, defer
 *    the scroll until RuntimePage finishes rendering the new page
 */
export function navigateOrScroll(
  navigate: (to: string) => void,
  href: string,
): void {
  const { slug, hash } = parseLinkTarget(href);
  if (hash && (!slug || document.getElementById(hash))) {
    scrollToId(hash);
    return;
  }
  if (!slug) return;
  if (hash) pendingScroll = hash;
  navigate(slug);
}
